import { useEffect, useRef, type ReactNode } from 'react';
import { cn } from '../../lib/utils.ts';
import { useLayer } from '../../lib/layers.ts';

/**
 * drawer/sheet מינימלי (בלי תלות): שכבה + לוח בצד ה-end (ב-RTL: שמאל) או מלמטה במובייל.
 * Escape (מחסנית השכבות — רק כשהלוח הוא העליון) ולחיצה על הרקע סוגרים; פוקוס עובר ללוח בפתיחה וחוזר בסגירה.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  side = 'end',
  className,
  testId,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  side?: 'start' | 'end';
  className?: string;
  testId?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useLayer(open, onClose);
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    return () => prev?.focus?.();
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-x-0 top-0 bottom-(--ab-foot) z-40">
      <div className="absolute inset-0 bg-[#181b34]/45 animate-[sheet-fade_150ms_ease-out]" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        data-testid={testId}
        className={cn(
          'absolute inset-y-0 flex w-full max-w-xl flex-col overflow-y-auto bg-background shadow-pop outline-none',
          side === 'end'
            ? 'end-0 border-s border-border animate-[sheet-in-end_200ms_cubic-bezier(.22,1,.36,1)]'
            : 'start-0 border-e border-border animate-[sheet-in-start_200ms_cubic-bezier(.22,1,.36,1)]',
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}
